import { Injectable } from '@angular/core';

import { MembershipCity } from '../models/membership.model';

const REF_KEY = 'lm.membership.ref';
const CITY_KEY = 'lm.membership.city';
const COUPON_KEY = 'lm.membership.coupon';

/**
 * What a member chose on the way to checkout, kept for the length of the tab.
 *
 * `?ref=` arrives on the plans page but is only USED after the number is verified, several screens
 * later — and a member may change plan in between. The city is remembered for the same reason: a
 * member who goes back to change the plan should not be asked where they live twice.
 *
 * sessionStorage, and every accessor wrapped, for the reasons `MembershipSessionService` gives.
 */
@Injectable({ providedIn: 'root' })
export class MembershipGrowthContextService {
  get referralToken(): string | null {
    return this.read(REF_KEY);
  }

  setReferralToken(token: string | null): void {
    if (token) {
      this.write(REF_KEY, token);
    } else {
      this.remove(REF_KEY);
    }
  }

  get city(): MembershipCity | null {
    const raw = this.read(CITY_KEY);
    if (!raw) {
      return null;
    }
    try {
      const city = JSON.parse(raw) as MembershipCity;
      return city?.cityId ? city : null;
    } catch {
      return null;
    }
  }

  setCity(city: MembershipCity | null): void {
    if (city) {
      // Only the three fields a screen reads, whatever else the caller's object carries (review MR !1 N25: store the minimum).
      const kept: MembershipCity = { cityId: city.cityId, name: city.name, state: city.state };
      this.write(CITY_KEY, JSON.stringify(kept));
    } else {
      this.remove(CITY_KEY);
    }
  }

  /**
   * An offer picked on the plans page, to be applied when the member reaches checkout (owner, 2026-10-28). Applied once and then forgotten:
   * the server still checks it for the plan and the member before it counts.
   */
  get pendingCoupon(): string | null {
    return this.read(COUPON_KEY);
  }

  setPendingCoupon(code: string | null): void {
    if (code) {
      this.write(COUPON_KEY, code);
    } else {
      this.remove(COUPON_KEY);
    }
  }

  /** Forgets both, as signing out must (review MR !1 N25): the next person on a shared phone starts with no invitation and no city. */
  clear(): void {
    this.remove(REF_KEY);
    this.remove(CITY_KEY);
    this.remove(COUPON_KEY);
  }

  private read(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable: the choice lives only for this page, which still works.
    }
  }

  private remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
